window.Accordion = (accordionId) => {
  return {
    active_section: null,
    // 'right' = new active is to the right of previous (slide in from the right).
    // 'left'  = new active is to the left of previous (slide in from the left).
    // null    = first render / no previous, no slide.
    slide_direction: null,

    init() {
      // Open the first tab by default. The selector finds the first AccordionTab block's
      // inner wrapper (which carries the randomId we toggle against).
      let selector =
        "#" + accordionId + " [id^='components-accordion-tab-block'] > div";
      let first = document.querySelector(selector);
      if (first) this.active_section = first.id;

      // Lock height to the tallest content panel so the row doesn't reflow when
      // switching tabs. Run on next tick + after images load (they affect height).
      this.$nextTick(() => this.lockHeight());
      window.addEventListener("load", () => this.lockHeight());
    },

    lockHeight() {
      const accordion = document.getElementById(accordionId);
      if (!accordion) return;

      // Target the actual flex row, not the outer wrapper — that's what dictates
      // tab heights via `align-items: stretch`.
      const row = accordion.querySelector(".accordion-innerblocks");
      if (!row) return;

      // Below the lg breakpoint the accordion stacks vertically and content
      // collapses with its own animation — locking a tall min-height there
      // would inflate the stack to the tallest panel's height.
      if (!window.matchMedia("(min-width: 1024px)").matches) {
        row.style.minHeight = "";
        return;
      }

      // Reference width = the currently-active tab's wrapper width. Every content
      // panel needs to be measured at THIS width because that's how wide the tab
      // gets when it's open. Measuring at the closed-tab width (narrow rotated
      // button) makes text wrap way more and inflates the height.
      const activeWrapper = this.active_section
        ? document.getElementById(this.active_section)
        : null;
      const referenceWidth = activeWrapper ? activeWrapper.offsetWidth : 0;
      if (!referenceWidth) return;

      let max = 0;

      accordion
        .querySelectorAll("[id^='accordion-tab-content-']")
        .forEach((el) => {
          const original = el.style.cssText;
          el.style.cssText =
            `display:block; height:auto; overflow:visible; visibility:hidden; position:absolute; width:${referenceWidth}px;`;
          if (el.offsetHeight > max) max = el.offsetHeight;
          el.style.cssText = original;
        });

      if (max > 0) row.style.minHeight = max + "px";
    },

    // Mobile only: park the opened panel's top just under the fixed header so the
    // user reads downward instead of scrolling back up past the collapsed tab.
    scrollContentToTop(content) {
      const header = document.querySelector("header");
      const offset = header ? header.offsetHeight : 0;
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      window.scrollTo({
        top: content.getBoundingClientRect().top + window.scrollY - offset,
        behavior: reduced ? "auto" : "smooth",
      });
    },

    /**
     * Open a tab and compute slide direction for the transition. Compares DOM
     * index of new vs current active to decide which side the new content
     * should slide in from.
     */
    setActive(tabId) {
      if (tabId === this.active_section) return;

      const accordion = document.getElementById(accordionId);
      const tabs = Array.from(
        accordion.querySelectorAll("[id^='components-accordion-tab-block'] > div")
      );
      const currentIdx = tabs.findIndex((t) => t.id === this.active_section);
      const newIdx = tabs.findIndex((t) => t.id === tabId);

      this.slide_direction = newIdx > currentIdx ? "right" : "left";

      // Below lg the accordion stacks vertically and content collapses with
      // a height transition; at lg+ it slides horizontally with a FLIP'd
      // closing panel and a leaving-button slide.
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

      // ============== CLOSING CONTENT ==============
      // Wipe any inline carryover from a prior enter so leave's class /
      // our new inline transition can take effect cleanly.
      let closing = null;
      let beforeRect = null;
      if (this.active_section) {
        closing = document.getElementById(
          "accordion-tab-content-" + this.active_section
        );
        if (closing) {
          closing.style.transition = "";
          closing.style.transform = "";
          closing.style.opacity = "";
          closing.style.width = "";
          closing.style.height = "";
          closing.style.overflow = "";

          if (isDesktop) {
            // FLIP setup: capture pre-swap viewport rect + freeze open width.
            beforeRect = closing.getBoundingClientRect();
            closing.style.width = closing.offsetWidth + "px";
          } else {
            // Mobile: snap to current height so we have a value to animate from.
            closing.style.height = closing.offsetHeight + "px";
            closing.style.overflow = "hidden";
            closing.style.transition = "height 400ms ease-out";
          }
        }
      }

      // ============== OPENING CONTENT ==============
      // Pre-apply enter-start state inline. Element is still display:none so
      // this is invisible; once Alpine flips x-show, the inline guarantees
      // the right starting frame with no race.
      const opening = document.getElementById("accordion-tab-content-" + tabId);
      if (opening) {
        opening.style.transition = "";
        opening.style.transform = "";
        opening.style.opacity = "";
        opening.style.width = "";
        opening.style.height = "";
        opening.style.overflow = "";

        if (isDesktop) {
          opening.style.opacity = "0";
          opening.style.transition = "none";
          opening.style.transform =
            newIdx > currentIdx ? "translateX(50%)" : "translateX(-50%)";
        } else {
          opening.style.height = "0px";
          opening.style.overflow = "hidden";
          opening.style.transition = "none";
        }
      }

      // ============== LEAVING BUTTON (desktop only) ==============
      // The new active's button fades out + slides as its tab opens — purely
      // a horizontal-layout effect, skipped on mobile where buttons stay put.
      let leavingButton = null;
      if (isDesktop && this.active_section) {
        const newWrapper = document.getElementById(tabId);
        if (newWrapper) {
          leavingButton = newWrapper.querySelector("button");
          if (leavingButton) {
            leavingButton.style.transition =
              "transform 400ms ease-out, opacity 400ms ease-out";

            if (newIdx > currentIdx) {
              const oldWrapper = document.getElementById(this.active_section);
              if (oldWrapper) {
                const shift = oldWrapper.offsetWidth - newWrapper.offsetWidth;
                leavingButton.style.left = shift + "px";
              }
            }
          }
        }
      }

      // ============== PREV BUTTON CLEANUP ==============
      // Wipe inline styles from the previously-active button before the class
      // swap so it fades back via the default opacity transition.
      if (this.active_section) {
        const prevWrapper = document.getElementById(this.active_section);
        if (prevWrapper) {
          const prevButton = prevWrapper.querySelector("button");
          if (prevButton && prevButton !== leavingButton) {
            prevButton.style.left = "";
            prevButton.style.transform = "";
            prevButton.style.transition = "";
          }
        }
      }

      this.active_section = tabId;

      // ============== POST-SWAP: CLOSING ==============
      if (closing) {
        if (isDesktop && beforeRect) {
          this.$nextTick(() => {
            const afterRect = closing.getBoundingClientRect();
            const dx = beforeRect.left - afterRect.left;
            const dy = beforeRect.top - afterRect.top;
            closing.style.transform = `translate(${dx}px, ${dy}px)`;
          });
        } else if (!isDesktop) {
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              closing.style.height = "0px";
            });
          });
        }
      }

      // ============== POST-SWAP: OPENING ==============
      if (opening) {
        if (isDesktop) {
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              opening.style.transition =
                "transform 400ms ease-out, opacity 400ms ease-out";
              opening.style.transform = "translateX(0)";
              opening.style.opacity = "1";
            });
          });
        } else {
          // Mobile: animate height from 0 → scrollHeight, then clear inline
          // so the panel can size naturally going forward (e.g. if its
          // contents change later).
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              opening.style.transition = "height 400ms ease-out";
              opening.style.height = opening.scrollHeight + "px";

              const cleanup = (e) => {
                if (e.propertyName !== "height") return;
                opening.style.height = "";
                opening.style.overflow = "";
                opening.style.transition = "";
                opening.removeEventListener("transitionend", cleanup);
                // Scroll once the closing panel has finished collapsing, or the
                // target position shifts out from under us mid-scroll.
                this.scrollContentToTop(opening);
              };
              opening.addEventListener("transitionend", cleanup);
            });
          });
        }
      }

      // ============== POST-SWAP: LEAVING BUTTON (desktop only) ==============
      if (leavingButton) {
        this.$nextTick(() => {
          const wrapperWidth = leavingButton.parentElement.offsetWidth;
          const sign = newIdx > currentIdx ? -1 : 1;
          leavingButton.style.transform =
            "translateX(" + sign * wrapperWidth + "px)";
        });
      }
    },
  };
};
